import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../apiBase';

function Avatar({ user, size }) {
  const [broken, setBroken] = useState(false);
  const px = size + 'px';
  if (user.avatar_url && !broken) {
    return (
      <img
        src={user.avatar_url}
        alt={user.username}
        onError={() => setBroken(true)}
        className="rounded-full border border-[#2c2c38] flex-shrink-0 object-cover"
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#2a2a36] to-[#16161e] border border-[#2c2c38] flex items-center justify-center font-black text-[#c9c9d6] flex-shrink-0"
      style={{ width: px, height: px, fontSize: Math.round(size * 0.42) }}
    >
      {user.username[0]?.toUpperCase()}
    </div>
  );
}

// Cases.gg, Winovo and Rain.gg are what the Benefits milestones are verified
// against, so each of those accounts can be linked to one profile only.
const ACCOUNT_FIELDS = [
  { key: 'casesggId', label: 'Cases.gg', placeholder: 'Your Cases.gg ID or username' },
  { key: 'winovoUsername', label: 'Winovo', placeholder: 'Your Winovo username' },
  { key: 'betdiceUsername', label: 'BetDice', placeholder: 'Enter your username' },
  { key: 'unboxId', label: 'Unbox.gg', placeholder: 'Enter your Unbox.gg ID' },
];
const RAINGG_FIELD = { key: 'raingId', label: 'Rain.gg', placeholder: 'Enter your Rain.gg ID' };

const WALLET_FIELDS = [
  { key: 'btcAddress', label: 'Bitcoin (BTC)', placeholder: 'Enter your BTC address' },
  { key: 'ethAddress', label: 'Ethereum (ETH)', placeholder: 'Enter your ETH address' },
  { key: 'ltcAddress', label: 'Litecoin (LTC)', placeholder: 'Enter your LTC address' },
  { key: 'solAddress', label: 'Solana (SOL)', placeholder: 'Enter your SOL address' },
];

const KEY_TO_COLUMN = {
  casesggId: 'casesgg_id',
  winovoUsername: 'winovo_username',
  betdiceUsername: 'betdice_username',
  unboxId: 'unbox_id',
  raingId: 'raingg_id',
  btcAddress: 'btc_address',
  ethAddress: 'eth_address',
  ltcAddress: 'ltc_address',
  solAddress: 'sol_address',
};

function Field({ field, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-bold text-white/80 mb-1.5">{field.label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(field.key, e.target.value)}
        placeholder={field.placeholder}
        className="w-full rounded-lg bg-[#181820] border border-[#353535] px-3 py-2.5 text-sm text-white placeholder:text-[#7d7d8c] focus:border-[#ff544a80]"
      />
    </div>
  );
}

export default function ProfileModal({ onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [memberSince, setMemberSince] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/profile')
      .then((r) => r.json())
      .then((p) => {
        const next = {};
        for (const key of Object.keys(KEY_TO_COLUMN)) next[key] = p[KEY_TO_COLUMN[key]] || '';
        setForm(next);
        setMemberSince(p.created_at);
      })
      .finally(() => setLoading(false));
  }, []);

  function set(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch('/api/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
      window.dispatchEvent(new Event('profile:saved')); // the Benefits page re-reads the linked accounts
      setToast('Saved!');
      setTimeout(() => setToast(''), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-[#24242e] max-h-[88vh] overflow-y-auto"
        style={{ background: 'linear-gradient(180deg,#14141c,#0c0c12)' }}
      >
        <div className="flex items-start justify-between p-6 pb-5">
          <div className="flex items-center gap-4">
            <Avatar user={user} size={64} />
            <div>
              <div className="font-display-lg text-xl font-bold text-white">{user.username}</div>
              <div className="text-sm text-[#888899] mt-0.5">🪙 {user.points.toLocaleString()} points</div>
              {memberSince && (
                <div className="text-xs text-[#7d7d8c] mt-0.5">
                  Member Since: {new Date(memberSince * 1000).toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' })}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-[#888899] hover:text-white text-xl leading-none cursor-pointer">×</button>
        </div>

        {loading ? (
          <div className="text-center text-sm text-[#7d7d8c] py-10">Loading…</div>
        ) : (
          <div className="px-6 pb-6 flex flex-col gap-6">
            <div>
              <h3 className="text-white font-bold text-base mb-3 pb-2 border-b border-[#24242e]">My Accounts</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                {ACCOUNT_FIELDS.map((f) => (
                  <Field key={f.key} field={f} value={form[f.key] || ''} onChange={set} />
                ))}
              </div>
              <Field field={RAINGG_FIELD} value={form[RAINGG_FIELD.key] || ''} onChange={set} />
            </div>

            <div>
              <h3 className="text-white font-bold text-base mb-3 pb-2 border-b border-[#24242e]">My Wallets</h3>
              <div className="grid grid-cols-2 gap-4">
                {WALLET_FIELDS.map((f) => (
                  <Field key={f.key} field={f} value={form[f.key] || ''} onChange={set} />
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-[#ff544a] text-center">{error}</p>}
            {toast && <p className="text-sm text-[#00E701] text-center">{toast}</p>}

            <button onClick={save} disabled={saving} className="btn-primary w-full rounded-lg py-3 text-sm">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
